import { NavLinkIconButton } from "@/components/ui/nav-link-icon-button";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { ArrowRight, LogIn, Menu, UserPlus2 } from "lucide-react";

const authButtonClassName = "h-10 rounded-none border-0 px-3 shadow-none";
const navLinks = [
  { to: "/features", label: "Features" },
  { to: "/about-us", label: "About" },
  { to: "/whats-new", label: "What's New" },
  { to: "/contact-us", label: "Contact" },
] as const;

export function MarketingNav() {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="container flex h-16 items-center justify-between gap-6">
        <Link to="/" className="flex items-center text-xl font-bold tracking-tight text-primary">
          <img
            src="/veronics_greenblack.png"
            alt="Veronics Logo"
            width="256"
            height="65"
            className="h-6 w-auto px-2 dark:hidden"
          />
          <img
            src="/veronics_cream.png"
            alt="Veronics Logo"
            width="256"
            height="65"
            className="hidden h-6 w-auto px-2 dark:block"
          />
        </Link>

        <nav className="hidden items-center gap-6 text-sm font-medium text-[hsl(28_33%_38%)] dark:text-muted-foreground md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="transition-colors hover:text-[hsl(28_33%_18%)] hover:underline hover:underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring motion-reduce:transition-none dark:hover:text-foreground"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center overflow-hidden rounded-md border border-border/60 bg-background shadow-sm [html[data-authenticated='true']_&]:hidden">
            <NavLinkIconButton
              to="/sign-up"
              icon={<UserPlus2 />}
              variant="navLinkGhost"
              className={cn(authButtonClassName, "border-r border-border/60")}
            >
              Sign up
            </NavLinkIconButton>
            <NavLinkIconButton to="/login" icon={<LogIn />} variant="navLinkGhost" className={authButtonClassName}>
              Login
            </NavLinkIconButton>
          </div>
          <div className="hidden items-center overflow-hidden rounded-md border border-border/60 bg-background shadow-sm [html[data-authenticated='true']_&]:flex">
            <NavLinkIconButton
              to="/organizations"
              icon={<ArrowRight />}
              variant="navLinkGhost"
              className={authButtonClassName}
            >
              Go to app
            </NavLinkIconButton>
          </div>
        </nav>

        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open navigation menu">
              <Menu className="size-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="top"
            className="max-h-[calc(100dvh-1rem)] w-full max-w-none overflow-y-auto border-b border-border/60 px-5 pb-6 pt-5 [&>button]:right-2 [&>button]:top-2 [&>button]:h-11 [&>button]:w-11 [&>button_svg]:h-5 [&>button_svg]:w-5"
          >
            <SheetHeader className="border-b border-border pb-4 pr-12 text-left">
              <SheetTitle>Navigation</SheetTitle>
            </SheetHeader>

            <div className="mt-6 flex flex-col gap-2">
              {navLinks.map((link) => (
                <SheetClose asChild key={link.to}>
                  <Link
                    to={link.to}
                    className="rounded-md p-3 text-base font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {link.label}
                  </Link>
                </SheetClose>
              ))}
            </div>

            <div className="mt-6 grid gap-3 border-t border-border pt-6 [html[data-authenticated='true']_&]:hidden">
              <SheetClose asChild>
                <NavLinkIconButton
                  to="/sign-up"
                  icon={<UserPlus2 />}
                  variant="outline"
                  className="w-full justify-center"
                >
                  Sign up
                </NavLinkIconButton>
              </SheetClose>
              <SheetClose asChild>
                <NavLinkIconButton
                  to="/login"
                  icon={<LogIn />}
                  variant="navLinkGhost"
                  className="w-full justify-center border border-border/60"
                >
                  Login
                </NavLinkIconButton>
              </SheetClose>
            </div>

            <div className="mt-6 hidden gap-3 border-t border-border pt-6 [html[data-authenticated='true']_&]:grid">
              <SheetClose asChild>
                <NavLinkIconButton
                  to="/organizations"
                  icon={<ArrowRight />}
                  variant="outline"
                  className="w-full justify-center"
                >
                  Go to app
                </NavLinkIconButton>
              </SheetClose>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
